import React from 'react';
import { LuEllipsis } from 'react-icons/lu';

import { Account, AccountType, CustodyType, LifecycleStatus } from '@ndvr/portfolio-api';

import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { usePageVersion } from '@/hooks/usePageVersion';
import { AdvisorLoginButton } from 'modules';
import { formatCurrency, getHouseholdName, titleCase, useRequiredParams } from 'utils';
import { useHouseholdRoutePaths } from 'utils/hooks/useHouseholdRoutePaths';
import { useAuthContext } from 'views/auth/Auth';
import { useGlobalAccount } from 'views/household/household-queries';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useAccount } from 'views/portfolio/account-queries';

const getLabLinkForAccount = (account?: Account): string => {
    if (account?.lifecycleStatus === LifecycleStatus.REAL) {
        return `/portfolio/${account.accountGUID}`;
    }
    return `/lab/${account?.accountGroupGUID}`;
};

const accountTaxLabels: Record<AccountType, string> = {
    [AccountType.TAXABLE]: 'Taxable',
    [AccountType.TRADITIONAL_IRA]: 'Tax Deferred',
    [AccountType.SEP_IRA]: 'Tax Deferred',
    [AccountType.TRADITIONAL_401K]: 'Tax Deferred',
    [AccountType.ROTH_IRA]: 'Tax Exempt',
    [AccountType.ROTH_401K]: 'Tax Exempt',
    [AccountType.TAX_EXEMPT]: 'Tax Exempt',
    [AccountType.CHARITY]: 'Tax Exempt',
    [AccountType.PLAN_529]: 'Tax Exempt',
};

export const AccountHeader = ({ showDetails = false }: { showDetails?: boolean }) => {
    const {
        HouseholdListPageRoutePath,
        HouseholdIndividualPageRoutePath,
        PortfolioOverviewTabRoutePath,
        PortfolioPageRoutePath,
    } = useHouseholdRoutePaths();
    const { globalAccountGUID, accountGroupGUID, accountGUID } = useRequiredParams([
        'globalAccountGUID',
        'accountGroupGUID',
        'accountGUID',
    ]);

    const { view } = usePageVersion();
    const hidePortfolioBreadcrumb = view === 'new';
    const { availableTenants } = useAuthContext();
    const { data: globalAccount } = useGlobalAccount(
        globalAccountGUID,
        showDetails ? { throwOnError: false } : undefined,
    );
    const { data: accountGroupData } = useAccountGroupProfileWithAumIncluded(
        accountGroupGUID,
        hidePortfolioBreadcrumb ? { throwOnError: false } : undefined,
    );
    const { data: accountData } = useAccount(accountGUID, showDetails ? { throwOnError: false } : undefined);

    const clientBaseName = availableTenants.find((tenant) => tenant.tenantGUID === globalAccount?.tenantGUID)?.name;
    const householdName = getHouseholdName(globalAccount);
    const portfolioName = accountGroupData?.accountGroup.accountGroupName?.plainText || 'Unnamed Portfolio';
    const accountName = accountData?.accountName?.plainText || 'Unnamed Account';
    const accountWithAum = accountGroupData?.accounts.find((account) => account.accountGUID === accountGUID);
    const details = accountData
        ? [
              accountData.custodyAccountId,
              accountData.custodyType === CustodyType.IB ? 'Interactive Brokers' : titleCase(accountData.custodyType),
              accountTaxLabels[accountData.accountType],
              formatCurrency(accountWithAum?.aum),
          ].filter(Boolean)
        : [];

    return (
        <>
            <PageTopBar />
            <PageTitleRow
                actions={
                    <div className="flex shrink-0 items-center gap-4">
                        <AdvisorLoginButton
                            globalAccountGUID={globalAccountGUID}
                            redirect={getLabLinkForAccount(accountData)}
                            variant="outline2"
                        />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline2" size="sm" className="rounded-full px-2" aria-label="Actions">
                                    <LuEllipsis className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {accountData?.accountGUID && (
                                    <CopyGuidDropdownMenuItem guid={accountData.accountGUID} />
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                }
            >
                <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-3">
                        <PageTitle>{accountName}</PageTitle>
                        <StatusBadge
                            status={accountData?.lifecycleStatus || LifecycleStatus.DRAFT}
                            closedAt={accountData?.closedAt}
                            liveDate={accountData?.liveDate}
                        />
                    </div>
                    <Breadcrumbs
                        parts={[
                            ...(clientBaseName ? [{ label: clientBaseName }] : []),
                            { label: 'Households', path: HouseholdListPageRoutePath },
                            {
                                label: householdName,
                                path:
                                    view === 'new'
                                        ? HouseholdIndividualPageRoutePath(globalAccountGUID, 'accounts')
                                        : PortfolioOverviewTabRoutePath(globalAccountGUID),
                            },
                            ...(!hidePortfolioBreadcrumb
                                ? [
                                      {
                                          label: portfolioName,
                                          path: PortfolioPageRoutePath(globalAccountGUID, accountGroupGUID),
                                      },
                                  ]
                                : []),
                            { label: accountName },
                        ]}
                    />
                    {showDetails && accountData && (
                        <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-2 pt-2 text-base">
                            <span>{details.join(' · ')}</span>
                            {accountGroupData?.accountGroup.allocationGUID && (
                                <span className="rounded-md bg-[#ad8058] px-3 py-1 text-xs font-medium tracking-widest text-white uppercase">
                                    Coordinated
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </PageTitleRow>
        </>
    );
};
